import ReactTextareaAutosize from "react-textarea-autosize";
import { useRef, useEffect, useState, type RefObject } from "react";
import { SendHorizontal } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../components/ui/tooltip";

const Examples = () => {
  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger className="text-[12px] font-bold text-gray-600 w-fit">Examples</TooltipTrigger>
        <TooltipContent align="end" className="border-gray-500">
          <div className="p-[5px]">
            <div className="p-[3px]">{`• Do any of my cases stand for [insert a proposition here]`}</div>
            <div className="p-[3px]">{`• Do any of my cases grant/deny the motion in part?`}</div>
            <div className="p-[3px]">{`• Distinguish
            cases I tagged as “Need to Distinguish” from these facts: [insert
            some here].`}</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export function useEnterSubmit(): {
  formRef: RefObject<HTMLFormElement>;
  onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
} {
  const formRef = useRef<HTMLFormElement>(null);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      formRef.current?.requestSubmit();
      event.preventDefault();
    }
  };

  return { formRef, onKeyDown: handleKeyDown };
}

export interface ChatInputProps {
  onSubmit: (value: string) => void;
  isLoading: boolean;
  isDisabled?: boolean;
  callCompareFacts?: () => void;
  getLongSummary?: () => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSubmit,
  isLoading,
  isDisabled,
  // callCompareFacts,
  // getLongSummary,
}) => {
  const [input, setInput] = useState<string>();
  const { formRef, onKeyDown } = useEnterSubmit();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [chatDropdownVisible, setChatDropdownVisible] = useState(false);

  const onSubmitInput = async (input?: string) => {
    if (!input?.trim()) {
      return;
    }
    await onSubmit(input);
    setInput("");
    textAreaRef?.current?.blur();
  };

  useEffect(() => {
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target && !target.closest(".chat-input-container") && !target.closest(".facts-popover-content")) {
        setChatDropdownVisible(false);
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        setChatDropdownVisible(false);
        textAreaRef?.current?.blur();
      }
    });
  }, []);

  return (
    <div className="relative chat-input-container">
      <form
        className={twMerge(
          "p-[10px] box-border w-full flex items-center border-[0.5px] border-gray-600 rounded-[7px] mb-0 overflow-hidden bg-gray-100",
          chatDropdownVisible && "rounded-b-none border-b-gray-100"
        )}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmitInput(input);
        }}
        ref={formRef}
      >
        <ReactTextareaAutosize
          // onFocus={() => {
          //   setChatDropdownVisible(true);
          // }}
          ref={textAreaRef}
          draggable={false}
          onKeyDown={onKeyDown}
          onChange={(v) => setInput(v.target.value)}
          placeholder={"Ask a question . . ."}
          spellCheck={false}
          maxRows={3}
          typeof="text"
          minRows={1}
          value={input}
          className="resize-none w-full outline-none text-black text-[14px] bg-transparent shrink-[1] border-none leading-normal shadow-none"
        />
        <button
          type="submit"
          title="send"
          disabled={isLoading || input === "" || isDisabled}
          className="flex items-center self-center justify-center bg-transparent border-none outline-none cursor-pointer min-w-fit"
        >
          <SendHorizontal size={16} />
        </button>
      </form>
      {/* <div className={twMerge("invisible", chatDropdownVisible && "visible")}>
        <ChatDropdown
          onSubmitInput={onSubmitInput}
          callCompareFacts={callCompareFacts!}
          getLongSummary={getLongSummary!}
        />
      </div> */}
    </div>
  );
};
